import { useTranslation } from "react-i18next";
import { useMadFormatter } from "../../config";
import { formatMad } from "../../internal/format";
import { stopBookingKind } from "../../lib/trip-booking";
import type { DiscoverItem } from "../../types";

/**
 * The price line under a stop: what the listing card would say about it.
 *
 * - A bookable activity or stay: "from" its lead price, in the guest's currency,
 *   the same number its card shows (the catalogue's dirham price through the
 *   host's formatter).
 * - A bookable restaurant: "Book a table". Tables are free, so never an amount.
 * - An explore place: nothing. It cannot be booked, so nothing invites it.
 */
export function StopPrice({
  item,
  className = "mj-trip-stop-price",
}: {
  item: DiscoverItem | null;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const format = useMadFormatter();
  const kind = stopBookingKind(item);
  if (!item || kind === "explore") return null;
  if (kind === "table") {
    return <span className={`${className} ${className}--table`}>{t("trip.price.book_table")}</span>;
  }
  const mad = item.priceMad;
  if (typeof mad !== "number" || mad <= 0) return null;
  const amount = format ? format(mad) : formatMad(mad, i18n.language);
  return (
    <span className={className}>
      {/* A hotel's lead price is a night's, and says so. */}
      {t(item.segment === "stays" && item.detailHref?.includes("/stays/") ? "trip.stay.from_night" : "trip.price.from", {
        price: amount,
      })}
    </span>
  );
}
